import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Map, Clock, CreditCard, Car, Check, Star, Download } from 'lucide-react';
// Placeholder for MiniParkingScene (commented out as it doesn't exist)
// import { MiniParkingScene } from '../components/MiniParkingScene';

const ParkingServicePage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    // Function to handle navigation
    const navigateTo = (page) => {
        navigate(`/${page}`);
    };

    return (
        <div className="min-h-screen bg-zonex-white text-zonex-gray-900">
            {/* Header Hero Section */}
            <section className="py-28 bg-gradient-to-br from-zonex-white to-zonex-lightGray relative overflow-hidden">
                {/* Red accent gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/10 via-transparent to-transparent z-0"></div>

                {/* Accent shapes */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                {/* Content */}
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">
                            Intelligent Parking Solutions <span className="text-zonex-red">for Urban Drivers</span>
                        </h1>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-lg text-zonex-gray-700 mb-8">
                            ZoneX's advanced parking management system eliminates the uncertainty and frustration of finding parking in busy urban areas. Our platform provides real-time availability, guaranteed reservations, and contactless access across thousands of locations.
                        </p>
                        <Link to="/">
                            <button className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center mx-auto shadow-md hover:shadow-lg">
                                Back to Home <ChevronRight className="w-5 h-5 ml-2" />
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* 3D Animation Section */}
            <section className="py-16 bg-zonex-lightGray relative overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="w-full h-96 rounded-xl overflow-hidden border border-zonex-red/30 shadow-red-lg">
                        {/* Placeholder for MiniParkingScene */}
                        <div className="w-full h-full flex items-center justify-center bg-black">
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            >
                                <source
                                    src="/"
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                <h3 className="text-white text-2xl font-bold">Smart Parking Visualization</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section ref={sectionRef} className="py-20 bg-zonex-white relative overflow-hidden">
                {/* Accent elements */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-zonex-red/5 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Advanced Parking Features</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Our intelligent parking platform combines cutting-edge technology with user-friendly design to transform the parking experience.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Map className="w-8 h-8 text-zonex-red" />,
                                title: "Real-time Availability Tracking",
                                description: "View available parking spots in real-time with accurate occupancy data updated every 60 seconds across all partner locations."
                            },
                            {
                                icon: <Car className="w-8 h-8 text-zonex-red" />,
                                title: "Guaranteed Spot Reservation",
                                description: "Secure your parking spot in advance with guaranteed availability and automated space allocation management."
                            },
                            {
                                icon: <Clock className="w-8 h-8 text-zonex-red" />,
                                title: "Flexible Duration Options",
                                description: "Choose from hourly, daily, weekly, or monthly parking plans with the ability to extend your stay directly from the app."
                            },
                            {
                                icon: <Check className="w-8 h-8 text-zonex-red" />,
                                title: "Contactless Digital Access",
                                description: "Seamless entry and exit using QR codes, license plate recognition, or Bluetooth technology without physical tickets."
                            },
                            {
                                icon: <Star className="w-8 h-8 text-zonex-red" />,
                                title: "Premium Location Network",
                                description: "Access exclusive parking spots in high-demand urban areas with prioritized availability for ZoneX members."
                            },
                            {
                                icon: <CreditCard className="w-8 h-8 text-zonex-red" />,
                                title: "Integrated Secure Payments",
                                description: "Hassle-free payment processing with multiple options and automatic receipts for business expense tracking."
                            }
                        ].map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-zonex-white p-6 rounded-xl border border-gray-200 hover:border-zonex-red/50 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all duration-500 group ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                    } transition-opacity duration-1000`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="mb-4 p-3 bg-zonex-red/10 rounded-lg group-hover:bg-zonex-red/20 transition-colors">{feature.icon}</div>
                                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                                <p className="text-zonex-gray-700 mb-6">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 bg-zonex-lightGray relative">
                {/* Enhanced accent elements */}
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-r from-zonex-red/20 to-zonex-lightGray opacity-30 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Seamless Parking Process</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Finding and securing parking with ZoneX is effortless. Follow these simple steps:
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                number: "01",
                                title: "Search Your Destination",
                                description: "Enter your destination or browse the interactive map to discover available parking locations nearby.",
                                icon: <Map className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "02",
                                title: "Compare & Reserve",
                                description: "View spot details, compare prices, and select your preferred option with guaranteed availability.",
                                icon: <Check className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "03",
                                title: "Navigate to Location",
                                description: "Follow turn-by-turn directions to your reserved parking spot with real-time traffic updates.",
                                icon: <Car className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "04",
                                title: "Contactless Experience",
                                description: "Use the app for seamless entry, payment, and exit without physical tickets or payment stations.",
                                icon: <CreditCard className="w-8 h-8 text-zonex-red" />
                            }
                        ].map((step, index) => (
                            <div
                                key={index}
                                className={`relative group ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} transition-all duration-1000`}
                                style={{ transitionDelay: `${index * 200}ms` }}
                            >
                                <div className="bg-zonex-white p-8 rounded-xl h-full flex flex-col hover:bg-gradient-to-b hover:from-zonex-white hover:to-zonex-lightGray transition-all duration-300 border border-gray-200 hover:border-zonex-red/50 shadow-md hover:shadow-lg">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="p-3 bg-zonex-red/10 rounded-lg group-hover:bg-zonex-red/20 transition-colors">
                                            {step.icon}
                                        </div>
                                        <span className="text-4xl font-bold text-zonex-red/20">{step.number}</span>
                                    </div>
                                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                                    <p className="text-zonex-gray-700">{step.description}</p>

                                    {/* Enhanced connector line with red gradient */}
                                    {index < 3 && (
                                        <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-1 bg-gradient-to-r from-zonex-red/80 to-zonex-red/10"></div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section - New */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-zonex-red/15 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-zonex-red/25 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose ZoneX Parking</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Our parking solutions deliver significant advantages for urban drivers
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="bg-zonex-lightGray p-6 rounded-xl border border-zonex-red/20 shadow-md">
                            <h3 className="text-2xl font-bold mb-6 flex items-center">
                                <Clock className="w-6 h-6 text-zonex-red mr-3" />
                                Time Savings
                            </h3>
                            <div className="pl-4 border-l-2 border-zonex-red/30">
                                <p className="text-zonex-gray-700 mb-4">
                                    The average driver spends 17 minutes searching for parking in urban areas. ZoneX eliminates this wasted time with guaranteed spot availability.
                                </p>
                                <div className="bg-white p-4 rounded-lg shadow-sm">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-zonex-gray-600">Traditional Parking</span>
                                        <span className="text-sm font-bold text-zonex-gray-900">17+ minutes</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                                        <div className="bg-gray-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                                    </div>

                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-zonex-gray-600">With ZoneX</span>
                                        <span className="text-sm font-bold text-zonex-red">Under 2 minutes</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-zonex-red h-2 rounded-full" style={{ width: '12%' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-zonex-lightGray p-6 rounded-xl border border-zonex-red/20 shadow-md">
                            <h3 className="text-2xl font-bold mb-6 flex items-center">
                                <CreditCard className="w-6 h-6 text-zonex-red mr-3" />
                                Cost Efficiency
                            </h3>
                            <div className="pl-4 border-l-2 border-zonex-red/30">
                                <p className="text-zonex-gray-700 mb-4">
                                    ZoneX users save an average of 15% on parking fees through our dynamic pricing system and loyalty rewards program.
                                </p>
                                <ul className="space-y-2">
                                    {["Transparent pricing with no hidden fees",
                                        "Special rates for frequent users",
                                        "Package deals for daily commuters",
                                        "Reduced fuel consumption from less circling"].map((item, i) => (
                                            <li key={i} className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                                                <Check className="w-5 h-5 text-zonex-red mr-3 flex-shrink-0" />
                                                <span className="text-zonex-gray-700">{item}</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-20 bg-zonex-lightGray relative">
                {/* Red accent shapes */}
                <div className="absolute top-0 right-1/4 w-1/5 h-1/5 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-1/3 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Users Say</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Hear from drivers who have transformed their urban parking experience with ZoneX.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {[
                                {
                                    text: "ZoneX has transformed my daily commute! I save at least 15 minutes every morning by booking my spot in advance, and the contactless entry system is effortless. Highly recommended for busy professionals.",
                                    author: "Priya Mehta",
                                    role: "Daily Commuter",
                                    rating: 5
                                },
                                {
                                    text: "As someone who frequently visits clients across the city, finding reliable parking used to be a nightmare. With ZoneX, I can book multiple spots throughout my day and never worry about being late to meetings.",
                                    author: "Rahul Singh",
                                    role: "Sales Executive",
                                    rating: 5
                                },
                                {
                                    text: "I appreciate the transparency in pricing. The app shows exactly what I'll pay upfront, and I've discovered several affordable parking options near my workplace that I didn't know existed before.",
                                    author: "Anjali Sharma",
                                    role: "Office Professional",
                                    rating: 4
                                },
                                {
                                    text: "The peace of mind knowing my parking is guaranteed during big events and busy weekends is worth every rupee. ZoneX has eliminated one of the biggest stressors of urban driving for me.",
                                    author: "Vikram Patel",
                                    role: "Weekend Explorer",
                                    rating: 5
                                }
                            ].map((testimonial, index) => (
                                <div
                                    key={index}
                                    className={`bg-zonex-white p-6 rounded-xl shadow-md border border-zonex-red/30 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                        } transition-all duration-1000`}
                                    style={{ transitionDelay: `${index * 200}ms` }}
                                >
                                    <div className="flex mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`w-5 h-5 ${i < testimonial.rating ? 'text-zonex-red fill-zonex-red' : 'text-gray-300'}`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-lg text-zonex-gray-700 mb-6 italic">"{testimonial.text}"</p>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 bg-zonex-red/20 rounded-full mr-4 flex items-center justify-center">
                                            <span className="text-zonex-red font-bold">{testimonial.author.charAt(0)}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{testimonial.author}</h4>
                                            <p className="text-sm text-zonex-red/80">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section - Added for SEO */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Find answers to common questions about ZoneX parking services
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        {[
                            {
                                question: "How do I reserve a parking spot with ZoneX?",
                                answer: "Reserving a parking spot is simple: download the ZoneX app, create an account, search for your destination, browse available parking locations, select your preferred spot, choose your parking duration, and complete the booking with secure payment. You'll receive a confirmation with a unique QR code for contactless access when you arrive."
                            },
                            {
                                question: "What happens if I need to extend my parking time?",
                                answer: "You can easily extend your parking duration directly through the ZoneX app. Simply open your active booking, select the 'Extend Stay' option, choose additional time, and confirm with payment. Extensions can be made anytime before your current reservation expires, subject to continued availability."
                            },
                            {
                                question: "Are there cancellation fees if my plans change?",
                                answer: "ZoneX offers flexible cancellation options. Cancellations made more than 12 hours before your reserved time qualify for a full refund. Cancellations within 12 hours but more than 2 hours before your booking receive a 70% refund. Cancellations less than 2 hours before may be subject to a cancellation fee, depending on the specific location's policy."
                            },
                            {
                                question: "How does the contactless entry system work?",
                                answer: "Our contactless system uses multiple technologies for seamless access. When you arrive at the parking location, open your ZoneX app to display your QR code. Scan this at the entry point or use our automatic license plate recognition system (where available). For Bluetooth-enabled locations, the app will automatically authenticate your device when you approach the entrance."
                            },
                            {
                                question: "Can I book parking for multiple days?",
                                answer: "Yes, ZoneX supports bookings for multiple days, weeks, or even monthly parking plans. When selecting your parking duration in the app, you can choose from hourly, daily, weekly, or monthly options. Long-term bookings often come with discounted rates compared to day-by-day reservations."
                            }
                        ].map((faq, index) => (
                            <div key={index} className="mb-6 bg-zonex-lightGray rounded-xl overflow-hidden shadow-md">
                                <div className="px-6 py-4 border-l-4 border-zonex-red">
                                    <h3 className="text-lg font-semibold text-zonex-gray-900">{faq.question}</h3>
                                </div>
                                <div className="px-6 py-4 bg-white">
                                    <p className="text-zonex-gray-700">{faq.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Parking Locations Section - Added for SEO */}
            <section className="py-20 bg-zonex-lightGray relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-1/4 h-1/4 bg-zonex-red/15 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Parking Network</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            ZoneX partners with premium parking facilities across major urban centers
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-zonex-gray-200">
                            <div className="h-48 bg-zonex-gray-300 relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-zonex-gray-600">Location Map: Central Business District</span>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">Central Business District</h3>
                                <p className="text-zonex-gray-700 mb-4">Over 25 premium locations with 24/7 security and covered parking options near major corporate offices and shopping centers.</p>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-zonex-gray-500">Starting from ₹80/hour</span>
                                    <button className="text-zonex-red text-sm font-medium hover:underline">View Locations</button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-zonex-gray-200">
                            <div className="h-48 bg-zonex-gray-300 relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-zonex-gray-600">Location Map: Entertainment District</span>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">Entertainment Zones</h3>
                                <p className="text-zonex-gray-700 mb-4">Convenient parking options near theaters, malls, restaurants, and nightlife venues with extended hours and valet services.</p>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-zonex-gray-500">Starting from ₹100/hour</span>
                                    <button className="text-zonex-red text-sm font-medium hover:underline">View Locations</button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-zonex-gray-200">
                            <div className="h-48 bg-zonex-gray-300 relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-zonex-gray-600">Location Map: Residential Areas</span>
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">Residential Districts</h3>
                                <p className="text-zonex-gray-700 mb-4">Monthly subscription plans for residents and daily visitors with guaranteed spots in high-density residential neighborhoods.</p>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-zonex-gray-500">Monthly plans from ₹3,000</span>
                                    <button className="text-zonex-red text-sm font-medium hover:underline">View Locations</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-zonex-red/10 to-transparent opacity-50"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="bg-gradient-to-br from-zonex-white to-zonex-lightGray rounded-2xl p-8 md:p-12 shadow-2xl border border-zonex-red/30">
                        <div className="md:flex items-center justify-between">
                            <div className="mb-8 md:mb-0 md:max-w-lg">
                                <h2 className="text-3xl md:text-4xl font-bold mb-4">Transform Your Parking Experience Today</h2>
                                <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
                                <p className="text-zonex-gray-700">
                                    Join thousands of drivers who have simplified their urban parking with ZoneX's intelligent platform. Never circle for a spot again!
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact">
                                    <button className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg">
                                        Download for iOS
                                    </button>
                                </Link>
                                <Link to="/contact">
                                    <button className="border border-zonex-red/50 text-zonex-red px-8 py-3 rounded-full hover:bg-zonex-red/10 transition-all duration-300 flex items-center justify-center">
                                        Download for Android
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Custom CSS */}
            <style jsx>{`
                @keyframes float-slow {
                    0% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                    100% { transform: translateY(0); }
                }
                
                @keyframes float-slow-reverse {
                    0% { transform: translateY(0); }
                    50% { transform: translateY(20px); }
                    100% { transform: translateY(0); }
                }
                
                .animate-float-slow {
                    animation: float-slow 8s ease-in-out infinite;
                }
                
                .animate-float-slow-reverse {
                    animation: float-slow-reverse 12s ease-in-out infinite;
                }
                
                .shadow-red-glow {
                    box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
                }
                
                .shadow-red-lg {
                    box-shadow: 0 10px 15px -3px rgba(252, 63, 63, 0.1), 0 4px 6px -2px rgba(252, 63, 63, 0.05);
                }
            `}</style>
        </div>
    );
};

export default ParkingServicePage;